/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { serviceTypes } from '@opengeoweb/store';
import {
  getWMSServiceId,
  invalidateWMSGetCapabilities,
  queryWMSLayers,
  queryWMSServiceInfo,
} from '@opengeoweb/webmap';
import {
  fetchStacCatalogRoot,
  fetchStacCatalogCollections,
  stacCatalogCollectionToLayerProps,
  defaultEdrStyles,
  fetchEDRJson,
  normalizeEDRLabel,
} from '@opengeoweb/webmap-react';
import type { layerSelectTypes } from '../../store';

export enum DetectableServiceType {
  STAC = 'STAC',
  EDR = 'EDR',
  WMS = 'WMS',
}

export type ValidationResult = string | boolean;

const stripStacCollectionsAndTrailingSlashes = (
  serviceUrl: string | null | undefined,
): string =>
  (serviceUrl ?? '').replace(/\/collections.*$/i, '').replace(/\/+$/, '');

export const normalizeStacServiceUrl = (
  serviceUrl: string | null | undefined,
): string => {
  const normalizedUrl = stripStacCollectionsAndTrailingSlashes(serviceUrl);
  return normalizedUrl ? `${normalizedUrl}/` : '';
};

export const validateServiceName = (
  t: TFunction,
  value: string,
  services: layerSelectTypes.ActiveServiceObjectEntities,
  ownServiceName = '',
): ValidationResult => {
  const serviceName = value?.toLowerCase();
  const foundName = Object.keys(services).find(
    (serviceId) =>
      services[serviceId]?.serviceName!.toLowerCase() === serviceName &&
      serviceName !== ownServiceName.toLocaleLowerCase(),
  );
  return foundName === undefined ? true : t('validations-name-existing');
};

const isValidUrl = (url: string): boolean => {
  if (url === '' || url === null || typeof url === 'undefined') {
    return false;
  }
  const matcher = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|[0-9a-zA-Z]+[:?\d]*)\S*$/;
  if (!matcher.test(url)) {
    return false;
  }
  return true;
};

export const validateServiceUrl = (
  t: TFunction,
  value: string,
  services: layerSelectTypes.ActiveServiceObjectEntities,
): ValidationResult => {
  const serviceUrl = value;
  // validate valid url
  const isServiceUrlValid = isValidUrl(serviceUrl);
  if (!isServiceUrlValid) {
    return t('validations-service-valid-url');
  }
  // check url existing
  const normalizedServiceUrl =
    stripStacCollectionsAndTrailingSlashes(serviceUrl);
  const foundUrl = Object.keys(services).find(
    (serviceId) =>
      stripStacCollectionsAndTrailingSlashes(
        services[serviceId]?.serviceUrl,
      ) === normalizedServiceUrl,
  );
  if (foundUrl) {
    return t('validations-service-existing');
  }

  return true;
};

export const loadWMSService = async (
  serviceUrl: string,
  forceReload = false,
): Promise<serviceTypes.SetLayersForServicePayload> => {
  if (forceReload) {
    await invalidateWMSGetCapabilities(serviceUrl);
  }

  const layers = await queryWMSLayers(serviceUrl);
  const service = await queryWMSServiceInfo(serviceUrl);

  return {
    id: service.id || getWMSServiceId(serviceUrl),
    name: service.title,
    serviceUrl,
    abstract: service.abstract,
    layers,
    scope: 'user',
  };
};

interface EDRParameter {
  id?: string;
  label?: string;
  description?: string;
  observedProperty?: { label?: string | Record<string, string> };
}

interface EDRCollection {
  id: string;
  title?: string;
  description?: string;
  path?: string[];
  groups?: string[];
  group?: string;
  parameter_names?: Record<string, EDRParameter> | EDRParameter[];
}

const getEDRParameters = (
  collection: EDRCollection,
): [string, EDRParameter][] => {
  if (Array.isArray(collection.parameter_names)) {
    return collection.parameter_names.map((parameter, index) => [
      parameter.id ?? String(index),
      parameter,
    ]);
  }
  return Object.entries(collection.parameter_names ?? {});
};

const getEDRPath = (collection: EDRCollection): string[] => [
  ...(collection.path ??
    collection.groups ??
    (collection.group ? [collection.group] : [])),
  collection.title ?? collection.id,
];

export const loadEDRService = async (
  serviceUrl: string,
): Promise<serviceTypes.SetLayersForServicePayload> => {
  const normalizedUrl = serviceUrl.replace(/\/$/, '');
  const [service, collectionsResponse] = await Promise.all([
    fetchEDRJson<{ title?: string; description?: string }>(normalizedUrl),
    fetchEDRJson<{ collections: EDRCollection[] }>(
      `${normalizedUrl}/collections`,
    ),
  ]);

  const serviceId = normalizedUrl.replace(/[^a-zA-Z0-9]/g, '_');
  const layers = collectionsResponse.collections.flatMap((collection) =>
    getEDRParameters(collection).map(([parameterId, parameter]) => ({
      name: parameterId,
      leaf: true,
      path: getEDRPath(collection),
      title: parameter.label ?? parameterId,
      abstract: parameter.description,
      keywords: parameter.observedProperty?.label
        ? [
            normalizeEDRLabel(
              parameter.observedProperty.label,
              parameter.label ?? parameterId,
            ),
          ]
        : [],
      // LayerSelect uses these fields when creating an EDR map layer.
      edrCollectionId: collection.id,
      edrParameter: parameter,
      styles: defaultEdrStyles,
    })),
  ) as serviceTypes.ServiceLayer[];

  return {
    id: serviceId,
    name: service.title ?? serviceId,
    serviceUrl: normalizedUrl,
    abstract: service.description ?? '',
    layers,
    scope: 'user',
    type: DetectableServiceType.EDR,
  };
};

const isEDRServiceUrl = async (serviceUrl: string): Promise<boolean> => {
  try {
    const service = await fetchEDRJson<{
      links?: { rel?: string; href?: string }[];
    }>(serviceUrl.replace(/\/$/, ''));
    return Boolean(
      service.links?.some(
        (link) =>
          (link.rel === 'data' || link.rel === 'collections') &&
          link.href?.includes('/collections'),
      ),
    );
  } catch {
    return false;
  }
};

export const detectServiceType = async (
  url: string,
): Promise<DetectableServiceType> => {
  const trimmedUrl = url.trim();
  const isStacServiceUrl = async (candidateUrl: string): Promise<boolean> => {
    try {
      const root = await fetchStacCatalogRoot(candidateUrl);
      const hasStacRootShape =
        root.stac_version ||
        root.type === 'Catalog' ||
        Array.isArray(root.conformsTo);

      if (hasStacRootShape) {
        return true;
      }

      return Boolean(
        root.type === 'Collection' &&
        root.stac_version &&
        Array.isArray(root.links),
      );
    } catch {
      return false;
    }
  };

  if (await isStacServiceUrl(trimmedUrl)) {
    return DetectableServiceType.STAC;
  }

  if (!trimmedUrl.endsWith('/') && (await isStacServiceUrl(`${trimmedUrl}/`))) {
    return DetectableServiceType.STAC;
  }

  if (await isEDRServiceUrl(trimmedUrl)) {
    return DetectableServiceType.EDR;
  }

  if (!trimmedUrl.endsWith('/') && (await isEDRServiceUrl(`${trimmedUrl}/`))) {
    return DetectableServiceType.EDR;
  }

  return DetectableServiceType.WMS;
};

export const loadSTACService = async (
  serviceUrl: string,
): Promise<serviceTypes.SetLayersForServicePayload> => {
  const [root, collections] = await Promise.all([
    fetchStacCatalogRoot(serviceUrl),
    fetchStacCatalogCollections(serviceUrl),
  ]);

  const serviceId = serviceUrl
    .replace(/\/+$/, '')
    .replace(/[^a-zA-Z0-9]/g, '_');

  const layers: serviceTypes.ServiceLayer[] = collections.map(
    stacCatalogCollectionToLayerProps,
  );

  return {
    // Use URL-based ids so different STAC endpoints with identical root.id
    // values (e.g. "stac-fastapi") can coexist in the service store.
    id: serviceId,
    name: root.title ?? root.id ?? serviceId,
    serviceUrl,
    abstract: root.description ?? '',
    layers,
    scope: 'user',
    type: DetectableServiceType.STAC,
  };
};
